import { TaggedError } from "better-result";
import { createStore, StoreApi } from "zustand";
import { combine } from "zustand/middleware";

import { promiseWithResolvers } from "./promise-with-resolvers";

export class RootStoreUnavailableError extends TaggedError("RootStoreUnavailableError")<{
  storeName: string;
  reason: "not_registered" | "not_initialized";
  message: string;
}>() {}

type StoreState = { init: () => Promise<void> };

type InitableStore<S extends StoreState> = {
  getState: () => S;
};

interface StoreItem<T> {
  item?: T;
  promiseInfo: {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (reason?: unknown) => void;
  };
  init: () => Promise<void>;
}

interface RootStore {
  stores: Record<string, StoreItem<InitableStore<StoreState>>>;
  state: "idle" | "initializing" | "initialized";
}

export function createRootStore() {
  return createStore(
    combine({ state: "idle", stores: {} } as RootStore, (set, get) => {
      return {
        async initAll() {
          if (get().state !== "idle") {
            return;
          }

          set({ state: "initializing" });
          const stores = get().stores;
          for (const store of Object.values(stores)) {
            await store.init();
          }
          set({ state: "initialized" });
        },
        async getStorePromise<T extends InitableStore<StoreState>>(name: string): Promise<T> {
          const store = get().stores[name];
          if (!store) {
            throw new RootStoreUnavailableError({
              storeName: name,
              reason: "not_registered",
              message: `Store ${name} is not registered`,
            });
          }
          return store.promiseInfo.promise as Promise<T>;
        },
        getStoreOrThrow<T extends InitableStore<StoreState>>(name: string): T {
          const store = get().stores[name];
          if (!store) {
            throw new RootStoreUnavailableError({
              storeName: name,
              reason: "not_registered",
              message: `Store ${name} is not registered`,
            });
          }
          if (!store.item) {
            throw new RootStoreUnavailableError({
              storeName: name,
              reason: "not_initialized",
              message: `Store ${name} is not initialized`,
            });
          }
          return store.item as T;
        },
        register<T extends InitableStore<StoreState>>(name: string, createStore: () => T) {
          if (get().stores[name]) {
            return;
          }

          const promise = promiseWithResolvers<InitableStore<StoreState>>();
          const init = async () => {
            const store = createStore();
            try {
              await store.getState().init();
              promise.resolve(store);

              const existingItem = get().stores[name];
              set((s) => ({ stores: { ...s.stores, [name]: { ...existingItem, item: store } } }));
            } catch (error) {
              promise.reject(error);
            }
          };
          const storeItem: StoreItem<InitableStore<StoreState>> = { promiseInfo: promise, init };
          set((s) => ({ stores: { ...s.stores, [name]: storeItem } }));
        },
      };
    })
  );
}

const rootStore = createRootStore();

const getRootStore = () => rootStore.getState();

export function createStoreGetters<S extends StoreState, T extends InitableStore<S> = StoreApi<S>>(
  name: string
) {
  return {
    getStore: () => getRootStore().getStoreOrThrow<T>(name).getState(),
    getStoreApi: () => getRootStore().getStoreOrThrow<T>(name),
    getStoreAsync: () =>
      getRootStore()
        .getStorePromise<T>(name)
        .then((store) => store.getState()),
  };
}

export function initializeRootStore() {
  return getRootStore().initAll();
}

export function registerStore<S extends StoreState, T extends InitableStore<S>>(
  name: string,
  createStore: () => T
) {
  getRootStore().register(name, createStore);
}
